import { useState, useEffect, useMemo } from "react";
import { goBack } from "@/lib/goBack";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/AppLayout";
import { Field } from "@/pages/Companies";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Save, Trash2, Plus, Sparkles, Wand2, Loader2, Package,
  User, Ruler, ListChecks, Percent, FileCheck, X, ArrowLeft, Mail
} from "lucide-react";
import EmailQuoteDialog from "@/components/EmailQuoteDialog";
import { fmtCurrencyExact, fmtCurrency, FENCE_LABELS } from "@/lib/constants";

const STEPS = [
  { id: 0, label: "Customer", icon: User },
  { id: 1, label: "Project", icon: Ruler },
  { id: 2, label: "Line items", icon: ListChecks },
  { id: 3, label: "Margin & summary", icon: Percent },
  { id: 4, label: "Review", icon: FileCheck },
];

const EMPTY_QUOTE = {
  customer_name: "", customer_email: "", customer_phone: "", site_address: "",
  company_id: "", contact_id: "", deal_id: "",
  fence_type: "chain_wire", linear_meters: 0, square_meters: 0,
  line_items: [], gp_margin_percent: 25, scope_summary: "", status: "draft", notes: "",
};

export default function QuoteBuilder() {
  const { id } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [step, setStep] = useState(0);
  const [quote, setQuote] = useState(EMPTY_QUOTE);
  const [aiLoading, setAiLoading] = useState(false);
  const [recLoading, setRecLoading] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerSearch, setPickerSearch] = useState("");
  const [emailOpen, setEmailOpen] = useState(false);

  const { data: fenceTypes = [] } = useQuery({ queryKey: ["fence-types"], queryFn: async () => (await api.get("/fence-types")).data });
  const { data: materials = [] } = useQuery({
    queryKey: ["materials-for-quote", quote.fence_type],
    queryFn: async () => (await api.get("/materials", { params: quote.fence_type ? { fence_type: quote.fence_type } : {} })).data,
  });
  const { data: companies = [] } = useQuery({ queryKey: ["companies-all-q"], queryFn: async () => (await api.get("/companies")).data });

  // Load if editing
  const { data: existing } = useQuery({
    queryKey: ["quote", id],
    queryFn: async () => (await api.get(`/quotes/${id}`)).data,
    enabled: !!id,
  });
  useEffect(() => {
    if (existing) setQuote(existing);
  }, [existing]);

  const totals = useMemo(() => {
    const items = quote.line_items || [];
    const cost = items.reduce((s, it) => s + (parseFloat(it.qty) || 0) * (parseFloat(it.unit_price) || 0), 0);
    const gpPct = Math.min(parseFloat(quote.gp_margin_percent) || 0, 99);
    const sale = gpPct < 100 ? cost / (1 - gpPct / 100) : cost;
    const gp = sale - cost;
    return { cost, sale, gp, gpPct };
  }, [quote.line_items, quote.gp_margin_percent]);

  const save = useMutation({
    mutationFn: async () => {
      const payload = {
        ...quote,
        company_id: quote.company_id || null,
        contact_id: quote.contact_id || null,
        deal_id: quote.deal_id || null,
        gp_margin_percent: parseFloat(quote.gp_margin_percent) || 0,
        linear_meters: parseFloat(quote.linear_meters) || 0,
        square_meters: parseFloat(quote.square_meters) || 0,
      };
      if (id) return (await api.patch(`/quotes/${id}`, payload)).data;
      return (await api.post("/quotes", payload)).data;
    },
    onSuccess: (data) => {
      toast.success(id ? "Quote updated" : `Quote ${data.quote_number} created`);
      qc.invalidateQueries({ queryKey: ["quotes"] });
      qc.invalidateQueries({ queryKey: ["quote", id] });
      if (!id) nav(`/quotes/${data.id}`);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Save failed"),
  });

  const addLine = (m) => {
    setQuote({
      ...quote,
      line_items: [...quote.line_items, {
        id: crypto.randomUUID(),
        material_id: m.id,
        name: m.name,
        category: m.category,
        unit: m.unit,
        qty: 1,
        unit_price: m.unit_price,
      }],
    });
    setPickerOpen(false);
  };

  const addCustomLine = () => {
    setQuote({
      ...quote,
      line_items: [...quote.line_items, {
        id: crypto.randomUUID(), material_id: null, name: "Custom item", category: "material", unit: "ea", qty: 1, unit_price: 0,
      }],
    });
  };

  const updateLine = (idx, patch) => {
    const items = [...quote.line_items];
    items[idx] = { ...items[idx], ...patch };
    setQuote({ ...quote, line_items: items });
  };
  const removeLine = (idx) => setQuote({ ...quote, line_items: quote.line_items.filter((_, i) => i !== idx) });

  const genSummary = async () => {
    setAiLoading(true);
    try {
      const { data } = await api.post("/ai/scope-summary", {
        fence_type: quote.fence_type,
        linear_meters: parseFloat(quote.linear_meters) || 0,
        square_meters: parseFloat(quote.square_meters) || 0,
        customer_name: quote.customer_name,
        site_address: quote.site_address,
        line_items: quote.line_items,
      });
      setQuote((q) => ({ ...q, scope_summary: data.summary }));
      toast.success("AI summary generated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI error");
    } finally { setAiLoading(false); }
  };

  const recommend = async () => {
    setRecLoading(true);
    try {
      const { data } = await api.post("/ai/recommend-materials", {
        fence_type: quote.fence_type,
        linear_meters: parseFloat(quote.linear_meters) || 0,
        square_meters: parseFloat(quote.square_meters) || 0,
      });
      const items = (data.items || []).map((it) => ({ ...it, id: crypto.randomUUID() }));
      if (items.length === 0) { toast.error("AI returned no items"); return; }
      setQuote((q) => ({ ...q, line_items: [...q.line_items, ...items] }));
      toast.success(`${items.length} recommended items added`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI error");
    } finally { setRecLoading(false); }
  };

  const filteredMaterials = materials.filter((m) => !pickerSearch || m.name.toLowerCase().includes(pickerSearch.toLowerCase()));

  return (
    <div>
      <PageHeader
        title={id ? `${quote.quote_number || "Edit Quote"}` : "Build Quote"}
        subtitle="Costing calculator with material master, GP margin, AI scope summary & smart recommendations."
        breadcrumb={["Sales", "Quotes", id ? "Edit" : "New"]}
        actions={
          <>
            <Button variant="outline" onClick={() => goBack(nav, "/quotes")} className="rounded-sm h-10"><ArrowLeft size={16} className="mr-1" /> Back</Button>
            {id && (
              <>
                <Button data-testid="print-quote-link" variant="outline" onClick={() => window.open(`/quotes/${id}/print`, "_blank")} className="rounded-sm h-10">
                  Print / PDF
                </Button>
                <Button data-testid="xero-export-btn" variant="outline" onClick={async () => {
                  try {
                    const { data } = await api.post(`/integrations/xero/export-quote/${id}`);
                    toast.success(data.message || "Exported to Xero");
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || "Xero not connected");
                  }
                }} className="rounded-sm h-10">
                  Export to Xero
                </Button>
                <Button data-testid="email-quote-btn" onClick={() => setEmailOpen(true)} className="rounded-sm h-10 bg-slate-900 hover:bg-slate-800 text-white">
                  <Mail size={16} className="mr-2" /> Email
                </Button>
                <Button data-testid="accept-quote-btn" onClick={async () => {
                  if (!window.confirm("Mark this quote as accepted and create a job?")) return;
                  try {
                    const { data } = await api.post(`/quotes/${id}/accept`);
                    toast.success(`Job ${data.job_number} created. Collect deposit to start.`);
                    qc.invalidateQueries({ queryKey: ["quote", id] });
                    qc.invalidateQueries({ queryKey: ["quotes"] });
                    nav(`/jobs/${data.id}`);
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || "Accept failed");
                  }
                }} className="rounded-sm h-10 bg-emerald-600 hover:bg-emerald-700 text-white">
                  <FileCheck size={16} className="mr-2" /> Accept &amp; Create Job
                </Button>
              </>
            )}
            <Button data-testid="save-quote-btn" onClick={() => save.mutate()} disabled={save.isPending} className="rounded-sm h-10 bg-cyan-600 hover:bg-cyan-700 text-white">
              {save.isPending ? <Loader2 size={16} className="animate-spin mr-2" /> : <Save size={16} className="mr-2" />}
              {id ? "Update quote" : "Save quote"}
            </Button>
          </>
        }
      />

      <div className="px-6 lg:px-10 py-6 grid lg:grid-cols-[1fr_360px] gap-6">
        {/* Main content */}
        <div>
          {/* Stepper */}
          <div className="flex items-center gap-1 mb-6 overflow-x-auto" data-testid="quote-stepper">
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              const active = step === s.id;
              const done = step > s.id;
              return (
                <button
                  key={s.id}
                  data-testid={`step-${s.id}`}
                  onClick={() => setStep(s.id)}
                  className={`flex items-center gap-2 px-3 h-10 text-xs font-semibold uppercase tracking-wider whitespace-nowrap border transition-colors ${active ? "bg-cyan-600 text-white border-cyan-600" : done ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-300 hover:border-slate-500"}`}
                >
                  <Icon size={14} />
                  <span>{i + 1}. {s.label}</span>
                </button>
              );
            })}
          </div>

          <Card className="rounded-sm border-slate-200 industrial-shadow p-6 min-h-[400px]">
            {step === 0 && (
              <div className="space-y-4">
                <h3 className="font-display text-lg font-semibold mb-2">Customer details</h3>
                <div className="grid sm:grid-cols-2 gap-3">
                  <Field label="Customer name" required><Input data-testid="quote-customer-name" value={quote.customer_name} onChange={(e) => setQuote({ ...quote, customer_name: e.target.value })} /></Field>
                  <Field label="Email"><Input type="email" value={quote.customer_email} onChange={(e) => setQuote({ ...quote, customer_email: e.target.value })} /></Field>
                  <Field label="Phone"><Input value={quote.customer_phone} onChange={(e) => setQuote({ ...quote, customer_phone: e.target.value })} /></Field>
                  <Field label="Link to company">
                    <Select value={quote.company_id || "none"} onValueChange={(v) => setQuote({ ...quote, company_id: v === "none" ? "" : v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— None —</SelectItem>
                        {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </Field>
                </div>
                <Field label="Site address"><Input value={quote.site_address} onChange={(e) => setQuote({ ...quote, site_address: e.target.value })} /></Field>
                <div className="flex justify-end pt-4 border-t border-slate-100">
                  <Button data-testid="step-next-btn" onClick={() => setStep(1)} disabled={!quote.customer_name} className="rounded-sm bg-slate-900 hover:bg-slate-800">Next →</Button>
                </div>
              </div>
            )}

            {step === 1 && (
              <div className="space-y-4">
                <h3 className="font-display text-lg font-semibold mb-2">Project specification</h3>
                <Field label="Fence / Gate type" required>
                  <Select value={quote.fence_type} onValueChange={(v) => setQuote({ ...quote, fence_type: v })}>
                    <SelectTrigger data-testid="quote-fence-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {fenceTypes.filter(f => !["labour","incidental"].includes(f.value)).map((f) => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <div className="grid sm:grid-cols-2 gap-3">
                  <Field label="Linear meters (fence)"><Input type="number" data-testid="quote-linear-meters" value={quote.linear_meters} onChange={(e) => setQuote({ ...quote, linear_meters: e.target.value })} /></Field>
                  <Field label="Square meters (gates)"><Input type="number" value={quote.square_meters} onChange={(e) => setQuote({ ...quote, square_meters: e.target.value })} /></Field>
                </div>
                <div className="bg-cyan-50 border border-cyan-200 p-4 mt-4">
                  <div className="flex items-start gap-3">
                    <Sparkles size={18} className="text-cyan-600 shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <div className="font-display text-sm font-semibold text-slate-900">AI Material Recommendations</div>
                      <div className="text-xs text-slate-600 mt-1">Auto-fill line items based on fence type, dimensions and past quote history.</div>
                    </div>
                    <Button
                      data-testid="ai-recommend-btn"
                      onClick={recommend}
                      disabled={recLoading || !quote.linear_meters}
                      className="rounded-sm bg-cyan-600 hover:bg-cyan-700"
                    >
                      {recLoading ? <Loader2 size={14} className="animate-spin" /> : <><Wand2 size={14} className="mr-1.5" /> Recommend</>}
                    </Button>
                  </div>
                </div>
                <div className="flex justify-between pt-4 border-t border-slate-100">
                  <Button variant="outline" onClick={() => setStep(0)} className="rounded-sm">← Back</Button>
                  <Button onClick={() => setStep(2)} className="rounded-sm bg-slate-900 hover:bg-slate-800">Next →</Button>
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-3">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-display text-lg font-semibold">Line items</h3>
                  <div className="flex gap-2">
                    <Button data-testid="add-custom-line-btn" variant="outline" size="sm" onClick={addCustomLine} className="rounded-sm"><Plus size={14} className="mr-1" /> Custom</Button>
                    <Button data-testid="add-from-master-btn" size="sm" onClick={() => setPickerOpen(true)} className="rounded-sm bg-slate-900 hover:bg-slate-800">
                      <Package size={14} className="mr-1" /> From Master
                    </Button>
                  </div>
                </div>

                {quote.line_items.length === 0 ? (
                  <div className="text-center py-12 border-2 border-dashed border-slate-200 text-sm text-slate-500">
                    No line items yet. Add from the material master or use AI recommendation.
                  </div>
                ) : (
                  <div className="border border-slate-200">
                    <div className="grid grid-cols-[1fr_80px_70px_100px_100px_30px] gap-2 px-3 py-2 bg-slate-50 stamp-label text-slate-500 border-b border-slate-200">
                      <div>Item</div><div className="text-right">Qty</div><div>Unit</div><div className="text-right">Unit $</div><div className="text-right">Total</div><div></div>
                    </div>
                    {quote.line_items.map((it, idx) => (
                      <div key={it.id} className="grid grid-cols-[1fr_80px_70px_100px_100px_30px] gap-2 px-3 py-2 items-center border-b border-slate-100 last:border-b-0 hover:bg-slate-50/40" data-testid={`line-item-${idx}`}>
                        <div>
                          <Input value={it.name} onChange={(e) => updateLine(idx, { name: e.target.value })} className="h-8 rounded-sm text-sm" />
                          <div className="flex gap-1 mt-0.5">
                            <Badge variant="outline" className="rounded-sm text-[9px] border-slate-200">{it.category}</Badge>
                          </div>
                        </div>
                        <Input type="number" step="0.1" value={it.qty} onChange={(e) => updateLine(idx, { qty: parseFloat(e.target.value) || 0 })} className="h-8 rounded-sm text-sm text-right" data-testid={`line-qty-${idx}`} />
                        <Input value={it.unit} onChange={(e) => updateLine(idx, { unit: e.target.value })} className="h-8 rounded-sm text-sm" />
                        <Input type="number" step="0.01" value={it.unit_price} onChange={(e) => updateLine(idx, { unit_price: parseFloat(e.target.value) || 0 })} className="h-8 rounded-sm text-sm text-right font-mono" />
                        <div className="text-right font-mono font-semibold text-sm text-slate-900">{fmtCurrencyExact((it.qty || 0) * (it.unit_price || 0))}</div>
                        <Button size="sm" variant="ghost" onClick={() => removeLine(idx)} className="h-7 w-7 p-0 text-rose-600" data-testid={`remove-line-${idx}`}><X size={13} /></Button>
                      </div>
                    ))}
                  </div>
                )}

                <div className="flex justify-between pt-4 border-t border-slate-100">
                  <Button variant="outline" onClick={() => setStep(1)} className="rounded-sm">← Back</Button>
                  <Button onClick={() => setStep(3)} className="rounded-sm bg-slate-900 hover:bg-slate-800">Next →</Button>
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-5">
                <h3 className="font-display text-lg font-semibold">Margin & scope summary</h3>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label className="stamp-label text-slate-600">GP Margin</Label>
                    <Input
                      type="number"
                      step="0.5"
                      data-testid="gp-margin-input"
                      value={quote.gp_margin_percent}
                      onChange={(e) => setQuote({ ...quote, gp_margin_percent: parseFloat(e.target.value) || 0 })}
                      className="w-24 h-9 rounded-sm text-right font-mono font-semibold"
                    />
                  </div>
                  <Slider
                    data-testid="gp-margin-slider"
                    min={0}
                    max={60}
                    step={0.5}
                    value={[quote.gp_margin_percent]}
                    onValueChange={(v) => setQuote({ ...quote, gp_margin_percent: v[0] })}
                  />
                  <div className="flex justify-between text-[10px] text-slate-400 mt-1 stamp-label">
                    <span>0%</span><span>30%</span><span>60%</span>
                  </div>
                </div>

                <div className="border border-slate-200 p-4 bg-slate-50/40">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <div className="stamp-label text-slate-500">Scope of works</div>
                      <div className="text-xs text-slate-500">AI-generated description for your quote.</div>
                    </div>
                    <Button data-testid="ai-summary-btn" size="sm" onClick={genSummary} disabled={aiLoading} className="rounded-sm bg-slate-900 hover:bg-slate-800">
                      {aiLoading ? <Loader2 size={13} className="animate-spin" /> : <><Sparkles size={13} className="mr-1.5" /> Generate</>}
                    </Button>
                  </div>
                  <Textarea
                    rows={6}
                    data-testid="scope-summary-textarea"
                    value={quote.scope_summary || ""}
                    onChange={(e) => setQuote({ ...quote, scope_summary: e.target.value })}
                    placeholder="Click Generate or write the scope manually..."
                    className="rounded-sm bg-white"
                  />
                </div>

                <Field label="Internal notes"><Textarea rows={2} value={quote.notes || ""} onChange={(e) => setQuote({ ...quote, notes: e.target.value })} /></Field>

                <div className="flex justify-between pt-4 border-t border-slate-100">
                  <Button variant="outline" onClick={() => setStep(2)} className="rounded-sm">← Back</Button>
                  <Button onClick={() => setStep(4)} className="rounded-sm bg-slate-900 hover:bg-slate-800">Review →</Button>
                </div>
              </div>
            )}

            {step === 4 && (
              <div className="space-y-5">
                <h3 className="font-display text-lg font-semibold">Review & finalise</h3>
                <div className="grid sm:grid-cols-2 gap-4 text-sm">
                  <ReviewItem label="Customer" value={quote.customer_name} />
                  <ReviewItem label="Site" value={quote.site_address || "—"} />
                  <ReviewItem label="Fence type" value={FENCE_LABELS[quote.fence_type]} />
                  <ReviewItem label="Linear meters" value={`${quote.linear_meters} m`} />
                  <ReviewItem label="Square meters (gates)" value={`${quote.square_meters} m²`} />
                  <ReviewItem label="Line items" value={`${quote.line_items.length}`} />
                </div>
                <div className="border border-slate-200 p-4 bg-cyan-50/30">
                  <div className="stamp-label text-slate-500 mb-1">Scope of works</div>
                  <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">{quote.scope_summary || "(none)"}</p>
                </div>
                <Field label="Status">
                  <Select value={quote.status} onValueChange={(v) => setQuote({ ...quote, status: v })}>
                    <SelectTrigger className="max-w-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="draft">Draft</SelectItem>
                      <SelectItem value="sent">Sent</SelectItem>
                      <SelectItem value="accepted">Accepted</SelectItem>
                      <SelectItem value="rejected">Rejected</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <div className="flex justify-between pt-4 border-t border-slate-100">
                  <Button variant="outline" onClick={() => setStep(3)} className="rounded-sm">← Back</Button>
                  <Button data-testid="finalize-quote-btn" onClick={() => save.mutate()} disabled={save.isPending} className="rounded-sm bg-cyan-600 hover:bg-cyan-700">
                    {save.isPending ? <Loader2 size={14} className="animate-spin" /> : <><Save size={14} className="mr-1.5" /> Save quote</>}
                  </Button>
                </div>
              </div>
            )}
          </Card>
        </div>

        {/* Sticky totals sidebar */}
        <aside className="lg:sticky lg:top-4 self-start">
          <Card className="rounded-sm border-slate-200 industrial-shadow overflow-hidden">
            <div className="bg-slate-900 text-white p-5">
              <div className="stamp-label text-cyan-400 mb-1">Quote Total</div>
              <div className="font-display text-4xl font-bold tracking-tight">{fmtCurrency(totals.sale)}</div>
              <div className="text-xs text-slate-400 mt-1 font-mono">AUD ex GST</div>
            </div>
            <div className="p-5 space-y-3 text-sm">
              <Row label="Total cost" value={fmtCurrencyExact(totals.cost)} />
              <Row label={`GP margin (${totals.gpPct.toFixed(1)}%)`} value={fmtCurrencyExact(totals.gp)} accent />
              <div className="h-px bg-slate-200 my-2" />
              <Row label="Sale price" value={fmtCurrencyExact(totals.sale)} bold />
              <div className="h-px bg-slate-200 my-2" />
              <div className="space-y-1 text-xs text-slate-500 font-mono">
                <div>Line items: {quote.line_items.length}</div>
                <div>Linear meters: {quote.linear_meters || 0} m</div>
              </div>
            </div>
          </Card>

          <div className="mt-3 text-xs text-slate-400 px-2 leading-relaxed">
            Sale price uses GP-on-sale: <span className="font-mono">sale = cost / (1 − GP%)</span>
          </div>
        </aside>
      </div>

      {/* Material picker dialog */}
      <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>        <DialogContent className="rounded-sm sm:max-w-2xl" data-testid="material-picker-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Add from Material Master</DialogTitle>
          </DialogHeader>
          <div className="text-xs text-slate-500 -mt-2 mb-2">Filtered by fence type: <b>{FENCE_LABELS[quote.fence_type]}</b></div>
          <Input data-testid="picker-search" placeholder="Search materials..." value={pickerSearch} onChange={(e) => setPickerSearch(e.target.value)} className="rounded-sm" />
          <div className="max-h-[400px] overflow-y-auto border border-slate-200 divide-y divide-slate-100">
            {filteredMaterials.map((m) => (
              <button key={m.id} onClick={() => addLine(m)} className="w-full text-left px-3 py-2 hover:bg-cyan-50 flex items-center justify-between gap-3" data-testid={`picker-item-${m.id}`}>
                <div className="min-w-0">
                  <div className="text-sm font-medium text-slate-900 truncate">{m.name}</div>
                  <div className="text-[10px] text-slate-500">{m.category} · {m.unit}</div>
                </div>
                <div className="font-mono text-sm font-semibold text-slate-900 shrink-0">{fmtCurrencyExact(m.unit_price)}</div>
              </button>
            ))}
            {filteredMaterials.length === 0 && <div className="p-6 text-center text-slate-400 text-sm">No matching materials</div>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPickerOpen(false)} className="rounded-sm">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <EmailQuoteDialog quote={{ ...quote, id }} open={emailOpen} onClose={() => setEmailOpen(false)} />
    </div>
  );
}

function Row({ label, value, accent, bold }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-slate-600 text-xs uppercase tracking-wider">{label}</span>
      <span className={`font-mono ${bold ? "font-bold text-lg text-slate-900" : accent ? "font-semibold text-cyan-600" : "text-slate-900"}`}>{value}</span>
    </div>
  );
}

function ReviewItem({ label, value }) {
  return (
    <div className="border border-slate-200 p-3">
      <div className="stamp-label text-slate-500">{label}</div>
      <div className="text-slate-900 font-medium mt-0.5">{value || "—"}</div>
    </div>
  );
}
